import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Home, Building, MapPin, Bed, Bath, PoundSterling, 
  ArrowLeft, ArrowRight, Check, Upload, X, Ruler,
  Car, Wifi, Thermometer, Trees, Shield, Image, Video, Trash2, Loader2,
  Sofa, Warehouse, Waves, Dumbbell, Wind, Lock, Key, Zap, Flame, Droplets,
  Building2, Castle, Hotel, Sparkles, Wand2, Info, Phone, Users
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AddListing = () => {
  const navigate = useNavigate();
  const { isArabic } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    listingType: '', // 'rent' or 'sale'
    propertyType: '', // 'house', 'flat', 'studio', etc.
    
    // Step 2: Location
    address: '',
    postcode: '',
    city: '',
    
    // Step 3: Property Details
    bedrooms: '',
    bathrooms: '',
    receptionRooms: '',
    floorArea: '',
    // New property details
    tenure: '', // freehold, leasehold
    councilTaxBand: '',
    epcRating: '',
    floorLevel: '',
    serviceCharge: '',
    groundRent: '',
    furnishing: 'unfurnished',
    
    // Step 4: Features & Amenities
    features: [],
    description: '',
    
    // Step 5: Pricing & Media
    price: '',
    availableFrom: '',
    depositAmount: '',
    minTenancy: '',
    images: [],  // Array of {url, filename, original_name}
    videos: [],  // Array of {url, filename, original_name}
    
    // Fee Structure
    serviceOption: 'standard', // 'standard' (free) or 'direct_contact' (paid opt-out)
    directContactFeePaid: false,
    
    // Landlord Contact (for Agency Agreement)
    landlordPhone: '',
    landlordAddress: ''
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('landlordLoggedIn');
    if (!isLoggedIn) {
      navigate('/landlord/login');
    }
  }, [navigate]);

  // Load saved landlord contact info on mount
  useEffect(() => {
    const storedPhone = localStorage.getItem('landlordPhone');
    const storedAddress = localStorage.getItem('landlordAddress');
    if (storedPhone) {
      setFormData(prev => ({ ...prev, landlordPhone: storedPhone }));
    }
    if (storedAddress) {
      setFormData(prev => ({ ...prev, landlordAddress: storedAddress }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const propertyTypes = [
    { id: 'detached', label: isArabic ? 'منزل منفصل' : 'Detached House', icon: Home },
    { id: 'semi-detached', label: isArabic ? 'منزل شبه منفصل' : 'Semi-Detached', icon: Home },
    { id: 'terraced', label: isArabic ? 'منزل متلاصق' : 'Terraced House', icon: Home },
    { id: 'flat', label: isArabic ? 'شقة' : 'Flat / Apartment', icon: Building },
    { id: 'studio', label: isArabic ? 'استوديو' : 'Studio', icon: Building },
    { id: 'bungalow', label: isArabic ? 'بنغالو' : 'Bungalow', icon: Home },
    { id: 'maisonette', label: isArabic ? 'ميزونيت' : 'Maisonette', icon: Building2 },
    { id: 'penthouse', label: isArabic ? 'بنتهاوس' : 'Penthouse', icon: Castle },
    { id: 'cottage', label: isArabic ? 'كوخ' : 'Cottage', icon: Home },
    { id: 'townhouse', label: isArabic ? 'تاون هاوس' : 'Townhouse', icon: Hotel },
    { id: 'hmo', label: isArabic ? 'سكن مشترك' : 'HMO', icon: Building },
    { id: 'commercial', label: isArabic ? 'تجاري' : 'Commercial', icon: Warehouse },
  ];

  const amenities = [
    { id: 'parking', label: isArabic ? 'موقف سيارات' : 'Parking', icon: Car },
    { id: 'garden', label: isArabic ? 'حديقة' : 'Garden', icon: Trees },
    { id: 'wifi', label: isArabic ? 'واي فاي' : 'WiFi Ready', icon: Wifi },
    { id: 'heating', label: isArabic ? 'تدفئة مركزية' : 'Central Heating', icon: Thermometer },
    { id: 'furnished', label: isArabic ? 'مفروش' : 'Furnished', icon: Sofa },
    { id: 'pets', label: isArabic ? 'مسموح بالحيوانات' : 'Pets Allowed', icon: Shield },
    { id: 'balcony', label: isArabic ? 'شرفة' : 'Balcony/Terrace', icon: Wind },
    { id: 'gym', label: isArabic ? 'صالة رياضية' : 'Gym Access', icon: Dumbbell },
    { id: 'pool', label: isArabic ? 'مسبح' : 'Swimming Pool', icon: Waves },
    { id: 'concierge', label: isArabic ? 'خدمة استقبال' : 'Concierge', icon: Key },
    { id: 'security', label: isArabic ? 'أمن 24 ساعة' : '24hr Security', icon: Lock },
    { id: 'dishwasher', label: isArabic ? 'غسالة صحون' : 'Dishwasher', icon: Droplets },
    { id: 'washer', label: isArabic ? 'غسالة' : 'Washing Machine', icon: Droplets },
    { id: 'aircon', label: isArabic ? 'تكييف' : 'Air Conditioning', icon: Wind },
    { id: 'epc_a', label: isArabic ? 'EPC تصنيف A' : 'EPC Rating A/B', icon: Zap },
    { id: 'gas', label: isArabic ? 'تدفئة بالغاز' : 'Gas Heating', icon: Flame },
    { id: 'ev_charging', label: isArabic ? 'شحن سيارة كهربائية' : 'EV Charging', icon: Zap },
    { id: 'double_glazing', label: isArabic ? 'زجاج مزدوج' : 'Double Glazing', icon: Home },
  ];

  const handleFeatureToggle = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  // Generate AI description
  const handleGenerateDescription = async () => {
    if (!formData.propertyType || !formData.city) {
      setUploadError('Please fill in property type and location first');
      return;
    }
    
    setGeneratingDescription(true);
    setUploadError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/generate-description`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_type: formData.propertyType,
          listing_type: formData.listingType,
          bedrooms: parseInt(formData.bedrooms) || 0,
          bathrooms: parseInt(formData.bathrooms) || 0,
          reception_rooms: parseInt(formData.receptionRooms) || 0,
          floor_area: parseInt(formData.floorArea) || 0,
          city: formData.city,
          postcode: formData.postcode,
          features: formData.features,
          tenure: formData.tenure,
          epc_rating: formData.epcRating
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          description: data.description
        }));
      } else {
        const error = await response.json();
        setUploadError(error.detail || 'Failed to generate description');
      }
    } catch (error) {
      console.error('AI description error:', error);
      setUploadError('Failed to generate description. Please try again.');
    }
    
    setGeneratingDescription(false);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    setUploadError(null);
    
    const formDataUpload = new FormData();
    files.forEach(file => {
      formDataUpload.append('files', file);
    });
    
    try {
      const response = await fetch(`${API_URL}/api/upload/multiple`, {
        method: 'POST',
        body: formDataUpload
      });
      
      if (response.ok) {
        const data = await response.json();
        const newImages = data.uploaded
          .filter(f => f.file_type === 'image')
          .map(f => ({
            url: `${API_URL}${f.url}`,
            filename: f.filename,
            original_name: f.original_name
          }));
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newImages]
        }));
        
        if (data.errors.length > 0) {
          setUploadError(`Some files failed to upload: ${data.errors.map(e => e.filename).join(', ')}`);
        }
      } else {
        const error = await response.json();
        setUploadError(error.detail || 'Failed to upload images');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload images. Please try again.');
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Handle video upload
  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    setUploadError(null);
    
    const formDataUpload = new FormData();
    files.forEach(file => {
      formDataUpload.append('files', file);
    });
    
    try {
      const response = await fetch(`${API_URL}/api/upload/multiple`, {
        method: 'POST',
        body: formDataUpload
      });
      
      if (response.ok) {
        const data = await response.json();
        const newVideos = data.uploaded
          .filter(f => f.file_type === 'video')
          .map(f => ({
            url: `${API_URL}${f.url}`,
            filename: f.filename,
            original_name: f.original_name
          }));
        
        setFormData(prev => ({
          ...prev,
          videos: [...prev.videos, ...newVideos]
        }));
        
        if (data.errors.length > 0) {
          setUploadError(`Some files failed to upload: ${data.errors.map(e => e.filename).join(', ')}`);
        }
      } else {
        const error = await response.json();
        setUploadError(error.detail || 'Failed to upload videos');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload videos. Please try again.');
    }
    
    setUploading(false);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  // Remove uploaded image
  const handleRemoveImage = async (index) => {
    const image = formData.images[index];
    
    // Try to delete from server
    try {
      await fetch(`${API_URL}/api/uploads/${image.filename}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Failed to delete from server:', error);
    }
    
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Remove uploaded video
  const handleRemoveVideo = async (index) => {
    const video = formData.videos[index];
    
    // Try to delete from server
    try {
      await fetch(`${API_URL}/api/uploads/${video.filename}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Failed to delete from server:', error);
    }
    
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.listingType || !formData.propertyType) {
      alert('Please select listing type and property type');
      return;
    }
    if (!formData.address || !formData.city || !formData.postcode) {
      alert('Please fill in the property address');
      return;
    }
    if (!formData.bedrooms || !formData.price) {
      alert('Please fill in bedrooms and price');
      return;
    }
    
    setSubmitting(true);
    
    // Calculate fee for direct contact option
    const price = parseFloat(formData.price) || 0;
    const directContactFee = formData.listingType === 'rent' 
      ? (price * 0.55) // 55% of first month's rent
      : (price * 0.021); // 2.1% of sale price
    
    // Get landlord info from localStorage
    const landlordId = localStorage.getItem('landlordId');
    const landlordName = localStorage.getItem('landlordName');
    const landlordEmail = localStorage.getItem('landlordEmail');
    
    // Save landlord contact for future use
    if (formData.landlordPhone) {
      localStorage.setItem('landlordPhone', formData.landlordPhone);
    }
    if (formData.landlordAddress) {
      localStorage.setItem('landlordAddress', formData.landlordAddress);
    }
    
    // Prepare listing data
    const newListing = {
      landlord_id: landlordId,
      landlord_name: landlordName,
      landlord_email: landlordEmail,
      landlord_phone: formData.landlordPhone || null,
      landlord_address: formData.landlordAddress || null,
      listing_type: formData.listingType,
      property_type: formData.propertyType,
      title: `${formData.bedrooms} Bedroom ${propertyTypes.find(p => p.id === formData.propertyType)?.label || 'Property'}`,
      address: formData.address,
      city: formData.city,
      postcode: formData.postcode,
      bedrooms: parseInt(formData.bedrooms) || 0,
      bathrooms: parseInt(formData.bathrooms) || 0,
      reception_rooms: parseInt(formData.receptionRooms) || 0,
      floor_area: parseInt(formData.floorArea) || 0,
      price: price,
      features: formData.features,
      description: formData.description,
      images: formData.images.map(img => img.url),
      videos: formData.videos.map(vid => vid.url),
      available_from: formData.availableFrom || null,
      furnishing: formData.furnishing || 'unfurnished',
      // Fee structure fields
      service_option: formData.serviceOption,
      direct_contact_enabled: formData.serviceOption === 'direct_contact',
      direct_contact_fee: formData.serviceOption === 'direct_contact' ? directContactFee : 0,
      direct_contact_fee_paid: false,
      deposit_amount: parseFloat(formData.depositAmount) || 0,
      min_tenancy: formData.minTenancy
    };

    console.log('Submitting listing:', newListing);

    try {
      const response = await fetch(`${API_URL}/api/listings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newListing)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Listing created:', result);
        
        // Also save to localStorage for backward compatibility
        const localListing = {
          id: Date.now(),
          ...formData,
          status: result.agreement_sent ? 'pending_agreement' : 'pending',
          views: 0,
          createdAt: new Date().toISOString(),
          title: newListing.title,
          image: formData.images[0]?.url || null
        };
        
        const existingListings = JSON.parse(localStorage.getItem('landlordListings') || '[]');
        existingListings.push(localListing);
        localStorage.setItem('landlordListings', JSON.stringify(existingListings));

        // Show success message with agreement info
        if (result.agreement_sent) {
          alert('🎉 Listing created!\n\nAn Agency Agreement has been sent to your email. Please sign and return it to activate your listing.\n\nCheck your inbox (and spam folder) for the agreement.');
        } else {
          alert('✅ Listing created successfully!');
        }
        
        navigate('/landlord/dashboard');
      } else {
        const error = await response.json();
        console.error('Server error:', error);
        alert(error.detail || 'Failed to create listing. Please try again.');
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error creating listing:', error);
      alert('Network error. Saving locally and redirecting...');
      
      // Fallback to localStorage only
      const localListing = {
        id: Date.now(),
        ...formData,
        status: 'pending',
        views: 0,
        createdAt: new Date().toISOString(),
        title: `${formData.bedrooms} Bedroom ${propertyTypes.find(p => p.id === formData.propertyType)?.label || 'Property'}`,
        image: formData.images[0]?.url || null
      };
      
      const existingListings = JSON.parse(localStorage.getItem('landlordListings') || '[]');
      existingListings.push(localListing);
      localStorage.setItem('landlordListings', JSON.stringify(existingListings));
      
      navigate('/landlord/dashboard');
    }
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4, 5].map((step) => (
        <React.Fragment key={step}>
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
              step < currentStep 
                ? 'bg-green-500 text-white' 
                : step === currentStep 
                  ? 'bg-[#FF8C42] text-white' 
                  : 'bg-gray-200 text-gray-500'
            }`}
          >
            {step < currentStep ? <Check className="w-5 h-5" /> : step}
          </div>
          {step < 5 && (
            <div className={`w-12 h-1 mx-1 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'ما نوع الإعلان؟' : 'What type of listing?'}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'اختر نوع الإعلان ونوع العقار' : 'Choose your listing type and property type'}
        </p>
      </div>

      {/* Listing Type */}
      <div className="grid grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setFormData({...formData, listingType: 'rent'})}
          data-testid="rent-option"
          className={`p-6 rounded-xl border-2 transition-all ${
            formData.listingType === 'rent'
              ? 'border-[#FF8C42] bg-[#FF8C42]/5'
              : 'border-gray-200 hover:border-[#FF8C42]/50'
          }`}
        >
          <Home className={`w-12 h-12 mx-auto mb-3 ${formData.listingType === 'rent' ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
          <p className="font-semibold text-[#404040]">{isArabic ? 'للإيجار' : 'For Rent'}</p>
          <p className="text-sm text-gray-500 mt-1">{isArabic ? 'أجر عقارك شهرياً' : 'Rent out your property monthly'}</p>
        </button>
        
        <button
          type="button"
          onClick={() => setFormData({...formData, listingType: 'sale'})}
          data-testid="sale-option"
          className={`p-6 rounded-xl border-2 transition-all ${
            formData.listingType === 'sale'
              ? 'border-[#FF8C42] bg-[#FF8C42]/5'
              : 'border-gray-200 hover:border-[#FF8C42]/50'
          }`}
        >
          <PoundSterling className={`w-12 h-12 mx-auto mb-3 ${formData.listingType === 'sale' ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
          <p className="font-semibold text-[#404040]">{isArabic ? 'للبيع' : 'For Sale'}</p>
          <p className="text-sm text-gray-500 mt-1">{isArabic ? 'بع عقارك' : 'Sell your property'}</p>
        </button>
      </div>

      {/* Property Type */}
      <div>
        <Label className="text-lg font-semibold text-[#404040] mb-4 block">
          {isArabic ? 'نوع العقار' : 'Property Type'}
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {propertyTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              data-testid={`${type.id}-option`}
              onClick={() => setFormData({...formData, propertyType: type.id})}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                formData.propertyType === type.id
                  ? 'border-[#FF8C42] bg-[#FF8C42]/5'
                  : 'border-gray-200 hover:border-[#FF8C42]/50'
              }`}
            >
              <type.icon className={`w-8 h-8 mb-2 ${formData.propertyType === type.id ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
              <p className="font-medium text-[#404040]">{type.label}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'موقع العقار' : 'Property Location'}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'أدخل عنوان العقار بالكامل' : 'Enter the full address of your property'}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="address">{isArabic ? 'العنوان' : 'Street Address'}</Label>
          <div className="relative mt-1">
            <MapPin className={`absolute top-3 ${isArabic ? 'right-3' : 'left-3'} w-5 h-5 text-gray-400`} />
            <Input
              id="address"
              placeholder={isArabic ? 'مثال: 25 شارع الرئيسي' : 'e.g., 25 Main Street'}
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className={`${isArabic ? 'pr-10' : 'pl-10'} py-6`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city">{isArabic ? 'المدينة' : 'City / Town'}</Label>
            <Input
              id="city"
              placeholder={isArabic ? 'مثال: هارو' : 'e.g., Harrow'}
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
              className="mt-1 py-6"
            />
          </div>
          <div>
            <Label htmlFor="postcode">{isArabic ? 'الرمز البريدي' : 'Postcode'}</Label>
            <Input
              id="postcode"
              placeholder={isArabic ? 'مثال: HA3 5AH' : 'e.g., HA3 5AH'}
              value={formData.postcode}
              onChange={(e) => setFormData({...formData, postcode: e.target.value})}
              className="mt-1 py-6"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'تفاصيل العقار' : 'Property Details'}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'أخبرنا عن مواصفات العقار' : 'Tell us about your property specifications'}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <Label htmlFor="bedrooms" className="flex items-center gap-2">
            <Bed className="w-5 h-5 text-[#FF8C42]" />
            {isArabic ? 'غرف النوم' : 'Bedrooms'}
          </Label>
          <Input
            id="bedrooms"
            type="number"
            min="0"
            placeholder="0"
            value={formData.bedrooms}
            onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
            className="mt-1 py-6 text-center text-xl"
            data-testid="bedrooms-input"
          />
        </div>

        <div>
          <Label htmlFor="bathrooms" className="flex items-center gap-2">
            <Bath className="w-5 h-5 text-[#FF8C42]" />
            {isArabic ? 'الحمامات' : 'Bathrooms'}
          </Label>
          <Input
            id="bathrooms"
            type="number"
            min="0"
            placeholder="0"
            value={formData.bathrooms}
            onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
            className="mt-1 py-6 text-center text-xl"
            data-testid="bathrooms-input"
          />
        </div>

        <div>
          <Label htmlFor="receptionRooms" className="flex items-center gap-2">
            <Sofa className="w-5 h-5 text-[#FF8C42]" />
            {isArabic ? 'غرف الاستقبال' : 'Reception Rooms'}
          </Label>
          <Input
            id="receptionRooms"
            type="number"
            min="0"
            placeholder="0"
            value={formData.receptionRooms}
            onChange={(e) => setFormData({...formData, receptionRooms: e.target.value})}
            className="mt-1 py-6 text-center text-xl"
            data-testid="reception-rooms-input"
          />
        </div>

        <div>
          <Label htmlFor="floorArea" className="flex items-center gap-2">
            <Ruler className="w-5 h-5 text-[#FF8C42]" />
            {isArabic ? 'المساحة (قدم²)' : 'Floor Area (sq ft)'}
          </Label>
          <Input
            id="floorArea"
            type="number"
            min="0"
            placeholder="0"
            value={formData.floorArea}
            onChange={(e) => setFormData({...formData, floorArea: e.target.value})}
            className="mt-1 py-6 text-center text-xl"
            data-testid="floor-area-input"
          />
        </div>
      </div>

      {/* Additional Property Details */}
      <div className="pt-6 border-t">
        <h3 className="text-lg font-semibold text-[#404040] mb-4">
          {isArabic ? 'تفاصيل إضافية' : 'Additional Details'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="tenure">{isArabic ? 'نوع الملكية' : 'Tenure'}</Label>
            <select
              id="tenure"
              value={formData.tenure}
              onChange={(e) => setFormData({...formData, tenure: e.target.value})}
              className="mt-1 w-full p-3 border rounded-lg bg-white"
              data-testid="tenure-select"
            >
              <option value="">{isArabic ? 'اختر...' : 'Select...'}</option>
              <option value="freehold">{isArabic ? 'ملكية حرة' : 'Freehold'}</option>
              <option value="leasehold">{isArabic ? 'إيجار طويل' : 'Leasehold'}</option>
              <option value="share_of_freehold">{isArabic ? 'حصة ملكية حرة' : 'Share of Freehold'}</option>
            </select>
          </div>

          <div>
            <Label htmlFor="councilTaxBand">{isArabic ? 'فئة ضريبة المجلس' : 'Council Tax Band'}</Label>
            <select
              id="councilTaxBand"
              value={formData.councilTaxBand}
              onChange={(e) => setFormData({...formData, councilTaxBand: e.target.value})}
              className="mt-1 w-full p-3 border rounded-lg bg-white"
              data-testid="council-tax-select"
            >
              <option value="">{isArabic ? 'اختر...' : 'Select...'}</option>
              {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(band => (
                <option key={band} value={band}>Band {band}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="epcRating">{isArabic ? 'تصنيف EPC' : 'EPC Rating'}</Label>
            <select
              id="epcRating"
              value={formData.epcRating}
              onChange={(e) => setFormData({...formData, epcRating: e.target.value})}
              className="mt-1 w-full p-3 border rounded-lg bg-white"
              data-testid="epc-rating-select"
            >
              <option value="">{isArabic ? 'اختر...' : 'Select...'}</option>
              {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(rating => (
                <option key={rating} value={rating}>{rating}</option>
              ))}
            </select>
          </div>

          {(formData.propertyType === 'flat' || formData.propertyType === 'maisonette' || formData.propertyType === 'penthouse') && (
            <div>
              <Label htmlFor="floorLevel">{isArabic ? 'الطابق' : 'Floor Level'}</Label>
              <Input
                id="floorLevel"
                type="text"
                placeholder={isArabic ? 'مثال: الثاني' : 'e.g., 2nd'}
                value={formData.floorLevel}
                onChange={(e) => setFormData({...formData, floorLevel: e.target.value})}
                className="mt-1"
                data-testid="floor-level-input"
              />
            </div>
          )}

          {formData.tenure === 'leasehold' && (
            <>
              <div>
                <Label htmlFor="serviceCharge">{isArabic ? 'رسوم الخدمة (سنوياً)' : 'Service Charge (yearly)'}</Label>
                <Input
                  id="serviceCharge"
                  type="number"
                  placeholder="£0"
                  value={formData.serviceCharge}
                  onChange={(e) => setFormData({...formData, serviceCharge: e.target.value})}
                  className="mt-1"
                  data-testid="service-charge-input"
                />
              </div>
              <div>
                <Label htmlFor="groundRent">{isArabic ? 'إيجار الأرض (سنوياً)' : 'Ground Rent (yearly)'}</Label>
                <Input
                  id="groundRent"
                  type="number"
                  placeholder="£0"
                  value={formData.groundRent}
                  onChange={(e) => setFormData({...formData, groundRent: e.target.value})}
                  className="mt-1"
                  data-testid="ground-rent-input"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'المميزات والوصف' : 'Features & Description'}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'اختر المميزات وأضف وصفاً للعقار' : 'Select features and add a property description'}
        </p>
      </div>

      {/* Amenities */}
      <div>
        <Label className="text-lg font-semibold text-[#404040] mb-4 block">
          {isArabic ? 'المميزات' : 'Amenities'}
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {amenities.map((amenity) => (
            <button
              key={amenity.id}
              type="button"
              onClick={() => handleFeatureToggle(amenity.id)}
              className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                formData.features.includes(amenity.id)
                  ? 'border-[#FF8C42] bg-[#FF8C42]/5'
                  : 'border-gray-200 hover:border-[#FF8C42]/50'
              }`}
            >
              <amenity.icon className={`w-6 h-6 ${formData.features.includes(amenity.id) ? 'text-[#FF8C42]' : 'text-gray-400'}`} />
              <span className="font-medium text-[#404040]">{amenity.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Description */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label htmlFor="description">{isArabic ? 'وصف العقار' : 'Property Description'}</Label>
          <Button
            type="button"
            onClick={handleGenerateDescription}
            disabled={generatingDescription}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm px-3 py-1.5 h-auto"
            data-testid="generate-description-btn"
          >
            {generatingDescription ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                {isArabic ? 'جاري الإنشاء...' : 'Generating...'}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-1" />
                {isArabic ? 'إنشاء بالذكاء الاصطناعي' : 'AI Generate'}
              </>
            )}
          </Button>
        </div>
        
        {uploadError && (
          <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {uploadError}
          </div>
        )}
        
        <Textarea
          id="description"
          placeholder={isArabic 
            ? 'صف العقار بالتفصيل... أو استخدم زر "إنشاء بالذكاء الاصطناعي" لإنشاء وصف تلقائي'
            : 'Describe your property in detail... or use "AI Generate" for an automatic description'
          }
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="mt-1 min-h-[150px]"
          data-testid="description-textarea"
        />
        <p className="text-xs text-gray-500 mt-1">
          {isArabic 
            ? 'نصيحة: قم بتعبئة تفاصيل العقار أولاً للحصول على وصف أفضل من الذكاء الاصطناعي'
            : 'Tip: Fill in property details first for a better AI-generated description'}
        </p>
      </div>
    </div>
  );

  const renderStep5 = () => {
    // Calculate fees based on listing type
    const price = parseFloat(formData.price) || 0;
    const directContactFee = formData.listingType === 'rent' 
      ? (price * 0.55) // 55% of first month's rent
      : (price * 0.021); // 2.1% of sale price
    const managementFeeAddition = formData.listingType === 'rent' 
      ? (price * 0.12) // 12% added to rent
      : 0;
    const tenantPays = price + managementFeeAddition;

    return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-[#404040] mb-2">
          {isArabic ? 'السعر والوسائط' : 'Price & Service Option'}
        </h2>
        <p className="text-gray-600">
          {isArabic ? 'حدد السعر وأضف الصور والفيديو' : 'Set your price and choose your service option'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="price" className="flex items-center gap-2">
            <PoundSterling className="w-5 h-5 text-[#FF8C42]" />
            {formData.listingType === 'rent' 
              ? (isArabic ? 'الإيجار الشهري المطلوب (£)' : 'Your Asking Rent (£/month)')
              : (isArabic ? 'سعر البيع (£)' : 'Sale Price (£)')
            }
          </Label>
          <Input
            id="price"
            type="number"
            min="0"
            placeholder={formData.listingType === 'rent' ? '1500' : '500000'}
            value={formData.price}
            onChange={(e) => setFormData({...formData, price: e.target.value})}
            className="mt-1 py-6 text-xl"
            data-testid="price-input"
          />
          {formData.listingType === 'rent' && formData.serviceOption === 'standard' && price > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              <Info className="w-4 h-4 inline mr-1" />
              Tenant will pay: <span className="font-bold text-[#FF8C42]">£{tenantPays.toLocaleString()}</span>/month
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="availableFrom">
            {isArabic ? 'متاح من' : 'Available From'}
          </Label>
          <Input
            id="availableFrom"
            type="date"
            value={formData.availableFrom}
            onChange={(e) => setFormData({...formData, availableFrom: e.target.value})}
            className="mt-1 py-6"
            data-testid="available-from-input"
          />
        </div>

        {/* Rental-specific fields */}
        {formData.listingType === 'rent' && (
          <>
            <div>
              <Label htmlFor="depositAmount">
                {isArabic ? 'مبلغ التأمين (£)' : 'Deposit Amount (£)'}
              </Label>
              <Input
                id="depositAmount"
                type="number"
                min="0"
                placeholder="e.g., 2000"
                value={formData.depositAmount}
                onChange={(e) => setFormData({...formData, depositAmount: e.target.value})}
                className="mt-1 py-6"
                data-testid="deposit-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="minTenancy">
                {isArabic ? 'الحد الأدنى للإيجار' : 'Minimum Tenancy'}
              </Label>
              <select
                id="minTenancy"
                value={formData.minTenancy}
                onChange={(e) => setFormData({...formData, minTenancy: e.target.value})}
                className="mt-1 w-full p-3 border rounded-lg bg-white"
                data-testid="min-tenancy-select"
              >
                <option value="">{isArabic ? 'اختر...' : 'Select...'}</option>
                <option value="6">{isArabic ? '6 أشهر' : '6 months'}</option>
                <option value="12">{isArabic ? '12 شهر' : '12 months'}</option>
                <option value="24">{isArabic ? '24 شهر' : '24 months'}</option>
                <option value="flexible">{isArabic ? 'مرن' : 'Flexible'}</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Landlord Contact Details (Required for Agency Agreement) */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-6">
          <h3 className="font-bold text-lg text-[#404040] mb-2 flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-500" />
            {isArabic ? 'بيانات التواصل' : 'Your Contact Details'}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {isArabic 
              ? 'مطلوب لإنشاء عقد الوكالة الذي سيتم إرساله إليك للتوقيع'
              : 'Required for the Agency Agreement that will be sent to you for signature'}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="landlordPhone" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                {isArabic ? 'رقم الهاتف' : 'Phone Number'} *
              </Label>
              <Input
                id="landlordPhone"
                type="tel"
                placeholder={isArabic ? 'مثال: 07123456789' : 'e.g., 07123 456789'}
                value={formData.landlordPhone}
                onChange={(e) => setFormData({...formData, landlordPhone: e.target.value})}
                className="mt-1"
                data-testid="landlord-phone-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="landlordAddress" className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                {isArabic ? 'عنوانك' : 'Your Address'} *
              </Label>
              <Input
                id="landlordAddress"
                type="text"
                placeholder={isArabic ? 'عنوانك الكامل' : 'Your full address'}
                value={formData.landlordAddress}
                onChange={(e) => setFormData({...formData, landlordAddress: e.target.value})}
                className="mt-1"
                data-testid="landlord-address-input"
                required
              />
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-100 rounded-lg">
            <p className="text-xs text-blue-800 flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Note:</strong> Once you submit your listing, an Agency Agreement will be automatically generated and sent to your email for signature. Your listing will go live after we receive your signed agreement.
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Service Option Selection */}
      <Card className="border-2 border-[#FF8C42]/30 bg-gradient-to-br from-orange-50 to-white">
        <CardContent className="p-6">
          <h3 className="font-bold text-lg text-[#404040] mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF8C42]" />
            {isArabic ? 'اختر خدمتك' : 'Choose Your Service'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Standard Option (Free) */}
            <button
              type="button"
              onClick={() => setFormData({...formData, serviceOption: 'standard', directContactFeePaid: false})}
              className={`p-5 rounded-xl border-2 transition-all text-left ${
                formData.serviceOption === 'standard'
                  ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                  : 'border-gray-200 hover:border-green-300'
              }`}
              data-testid="standard-service-option"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  {isArabic ? 'مجاني' : 'FREE'}
                </span>
                {formData.serviceOption === 'standard' && (
                  <Check className="w-6 h-6 text-green-500" />
                )}
              </div>
              <h4 className="font-bold text-[#404040] mb-2">
                {isArabic ? 'الخدمة الكاملة' : 'Full Management Service'}
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>We handle all tenant enquiries & viewings</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Professional tenant vetting included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Full property management</span>
                </li>
              </ul>
              {formData.listingType === 'rent' && price > 0 && (
                <div className="mt-4 p-3 bg-white rounded-lg border">
                  <p className="text-xs text-gray-500">How it works:</p>
                  <p className="text-sm">
                    You receive: <span className="font-bold text-green-600">£{price.toLocaleString()}/month</span>
                  </p>
                  <p className="text-sm">
                    Tenant pays: <span className="font-bold">£{tenantPays.toLocaleString()}/month</span>
                    <span className="text-xs text-gray-500 ml-1">(+12% management fee)</span>
                  </p>
                </div>
              )}
            </button>

            {/* Direct Contact Option (Paid) */}
            <button
              type="button"
              onClick={() => setFormData({...formData, serviceOption: 'direct_contact'})}
              className={`p-5 rounded-xl border-2 transition-all text-left ${
                formData.serviceOption === 'direct_contact'
                  ? 'border-[#FF8C42] bg-orange-50 ring-2 ring-orange-200'
                  : 'border-gray-200 hover:border-[#FF8C42]/50'
              }`}
              data-testid="direct-contact-option"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="bg-[#FF8C42] text-white px-3 py-1 rounded-full text-sm font-bold">
                  {isArabic ? 'رسوم لمرة واحدة' : 'ONE-TIME FEE'}
                </span>
                {formData.serviceOption === 'direct_contact' && (
                  <Check className="w-6 h-6 text-[#FF8C42]" />
                )}
              </div>
              <h4 className="font-bold text-[#404040] mb-2">
                {isArabic ? 'التواصل المباشر' : 'Direct Contact (Tenant Finder)'}
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <Phone className="w-4 h-4 text-[#FF8C42] flex-shrink-0 mt-0.5" />
                  <span>Handle enquiries yourself</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#FF8C42] flex-shrink-0 mt-0.5" />
                  <span>We advertise & find tenants/buyers</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#FF8C42] flex-shrink-0 mt-0.5" />
                  <span>No ongoing management fee</span>
                </li>
              </ul>
              {price > 0 && (
                <div className="mt-4 p-3 bg-white rounded-lg border border-[#FF8C42]/30">
                  <p className="text-xs text-gray-500">One-time fee:</p>
                  <p className="text-xl font-bold text-[#FF8C42]">
                    £{directContactFee.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formData.listingType === 'rent' 
                      ? '55% of first month\'s rent' 
                      : '2.1% of sale price'}
                  </p>
                </div>
              )}
            </button>
          </div>

          {formData.serviceOption === 'direct_contact' && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 flex items-start gap-2">
                <Info className="w-5 h-5 flex-shrink-0" />
                <span>
                  <strong>Payment Required:</strong> The one-time fee of{' '}
                  <strong>£{directContactFee.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</strong>{' '}
                  must be paid before your listing goes live. Our team will contact you for payment after submission.
                </span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Message */}
      {uploadError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {uploadError}
        </div>
      )}

      {/* Photo Upload */}
      <div>
        <Label className="flex items-center gap-2 mb-2">
          <Image className="w-5 h-5 text-[#FF8C42]" />
          {isArabic ? 'صور العقار' : 'Property Photos'}
        </Label>
        
        {/* Uploaded Images Preview */}
        {formData.images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {formData.images.map((image, index) => (
              <div key={index} className="relative group">
                <img 
                  src={image.url} 
                  alt={image.original_name}
                  className="w-full h-24 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
                <p className="text-xs text-gray-500 truncate mt-1">{image.original_name}</p>
              </div>
            ))}
          </div>
        )}
        
        {/* Upload Button */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
          multiple
          onChange={handleImageUpload}
          className="hidden"
          data-testid="photo-upload-input"
        />
        <div 
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
            uploading ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-[#FF8C42]'
          }`}
          data-testid="photo-upload-dropzone"
        >
          {uploading ? (
            <Loader2 className="w-10 h-10 text-[#FF8C42] mx-auto mb-2 animate-spin" />
          ) : (
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          )}
          <p className="text-gray-600">
            {uploading 
              ? (isArabic ? 'جاري التحميل...' : 'Uploading...')
              : (isArabic ? 'اسحب الصور هنا أو انقر للتحميل' : 'Drag photos here or click to upload')
            }
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {isArabic ? 'من الكاميرا أو المعرض - حتى 50MB لكل ملف' : 'From camera or gallery - up to 50MB each'}
          </p>
        </div>
      </div>

      {/* Video Upload */}
      <div>
        <Label className="flex items-center gap-2 mb-2">
          <Video className="w-5 h-5 text-[#FF8C42]" />
          {isArabic ? 'فيديو العقار' : 'Property Videos'}
        </Label>
        
        {/* Uploaded Videos Preview */}
        {formData.videos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {formData.videos.map((video, index) => (
              <div key={index} className="relative group">
                <video 
                  src={video.url}
                  className="w-full h-32 object-cover rounded-lg border"
                  controls
                />
                <button
                  type="button"
                  onClick={() => handleRemoveVideo(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
                <p className="text-xs text-gray-500 truncate mt-1">{video.original_name}</p>
              </div>
            ))}
          </div>
        )}
        
        {/* Upload Button */}
        <input
          ref={videoInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mov,.mp4,.avi"
          multiple
          onChange={handleVideoUpload}
          className="hidden"
          data-testid="video-upload-input"
        />
        <div 
          onClick={() => !uploading && videoInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
            uploading ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-[#FF8C42]'
          }`}
          data-testid="video-upload-dropzone"
        >
          {uploading ? (
            <Loader2 className="w-10 h-10 text-[#FF8C42] mx-auto mb-2 animate-spin" />
          ) : (
            <Video className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          )}
          <p className="text-gray-600">
            {uploading 
              ? (isArabic ? 'جاري التحميل...' : 'Uploading...')
              : (isArabic ? 'اسحب الفيديو هنا أو انقر للتحميل' : 'Drag videos here or click to upload')
            }
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {isArabic ? 'من الكاميرا أو المعرض - حتى 50MB لكل ملف' : 'From camera or gallery - up to 50MB each'}
          </p>
        </div>
      </div>

      {/* Summary */}
      <Card className="bg-gray-50">
        <CardContent className="p-6">
          <h3 className="font-semibold text-[#404040] mb-4">
            {isArabic ? 'ملخص الإعلان' : 'Listing Summary'}
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">{isArabic ? 'نوع الإعلان' : 'Listing Type'}:</span>
              <span className="font-medium">{formData.listingType === 'rent' ? (isArabic ? 'للإيجار' : 'For Rent') : (isArabic ? 'للبيع' : 'For Sale')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{isArabic ? 'نوع العقار' : 'Property Type'}:</span>
              <span className="font-medium">{propertyTypes.find(p => p.id === formData.propertyType)?.label || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{isArabic ? 'الموقع' : 'Location'}:</span>
              <span className="font-medium">{formData.city || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{isArabic ? 'غرف النوم' : 'Bedrooms'}:</span>
              <span className="font-medium">{formData.bedrooms || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{isArabic ? 'السعر' : 'Your Asking Price'}:</span>
              <span className="font-medium text-[#FF8C42]">
                £{parseInt(formData.price || 0).toLocaleString()}{formData.listingType === 'rent' ? '/mo' : ''}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{isArabic ? 'خيار الخدمة' : 'Service Option'}:</span>
              <span className={`font-medium ${formData.serviceOption === 'standard' ? 'text-green-600' : 'text-[#FF8C42]'}`}>
                {formData.serviceOption === 'standard' ? 'Full Management (Free)' : 'Direct Contact (Paid)'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{isArabic ? 'الصور' : 'Photos'}:</span>
              <span className="font-medium">{formData.images.length} {isArabic ? 'صورة' : 'uploaded'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{isArabic ? 'الفيديو' : 'Videos'}:</span>
              <span className="font-medium">{formData.videos.length} {isArabic ? 'فيديو' : 'uploaded'}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#404040]">
            {isArabic ? 'إضافة عقار جديد' : 'Add New Listing'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isArabic 
              ? 'أضف عقارك للإيجار أو البيع في دقائق'
              : 'List your property for rent or sale in minutes'
            }
          </p>
        </div>

        {/* Step Indicator */}
        {renderStepIndicator()}

        {/* Form Card */}
        <Card className="border-none shadow-xl">
          <CardContent className="p-8">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}

            {/* Navigation Buttons */}
            <div className={`flex justify-between mt-8 pt-6 border-t ${isArabic ? 'flex-row-reverse' : ''}`}>
              {currentStep > 1 ? (
                <Button variant="outline" onClick={prevStep} className="px-6">
                  <ArrowLeft className={`w-5 h-5 ${isArabic ? 'ml-2 rotate-180' : 'mr-2'}`} />
                  {isArabic ? 'السابق' : 'Previous'}
                </Button>
              ) : (
                <Link to="/landlord/dashboard">
                  <Button variant="outline" className="px-6">
                    <X className={`w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                    {isArabic ? 'إلغاء' : 'Cancel'}
                  </Button>
                </Link>
              )}

              {currentStep < totalSteps ? (
                <Button 
                  onClick={nextStep} 
                  className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white px-6"
                  disabled={
                    (currentStep === 1 && (!formData.listingType || !formData.propertyType)) ||
                    (currentStep === 2 && (!formData.address || !formData.city))
                  }
                >
                  {isArabic ? 'التالي' : 'Next'}
                  <ArrowRight className={`w-5 h-5 ${isArabic ? 'mr-2 rotate-180' : 'ml-2'}`} />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit} 
                  className="bg-green-500 hover:bg-green-600 text-white px-8"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className={`w-5 h-5 animate-spin ${isArabic ? 'ml-2' : 'mr-2'}`} />
                      {isArabic ? 'جاري الإرسال...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <Check className={`w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                      {isArabic ? 'نشر الإعلان' : 'Publish Listing'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
